import { Howl, Howler } from 'howler';

// Prevent Safari from suspending AudioContext in background tabs
Howler.autoSuspend = false;

const MUSIC_BASE = process.env.REACT_APP_MUSIC_BASE_URL;

// Cache buster — change this when music files are replaced on S3
const MUSIC_VERSION = 'v2';

// Sound effects served locally from /public/sfx/ for zero-latency playback
const SFX_NAMES = [
  'correct', 'wrong', 'click', 'flip', 'match',
  'win', 'tick', 'drop', 'collapse', 'goal',
  'whoosh', 'streak', 'celebration'
];

const SFX_VOLUME = 0.45;
const MUSIC_VOLUME = 0.18;

// Game → music track mapping
const GAME_MUSIC = {
  la_course: 'playful',
  batisseur: 'playful',
  tri_express: 'playful',
  avalanche: 'playful',
  le_chef: 'playful',
  duel_conjugaison: 'competitive',
  la_tour: 'competitive',
  detective: 'mystery',
  carte_du_canada: 'mystery',
  memoire: 'calm',
  horloge_folle: 'calm',
  robot_balayeur: 'calm',
  endscreen: 'calm',
};

// Eagerly create ALL SFX Howl instances (local files = instant load)
const sfxHowls = {};
SFX_NAMES.forEach(name => {
  sfxHowls[name] = new Howl({
    src: [`/sfx/${name}.ogg`, `/sfx/${name}.mp3`],
    volume: SFX_VOLUME,
    preload: true,
  });
});

// Ensure AudioContext is running before any play
function ensureAudioContext() {
  if (Howler.ctx && Howler.ctx.state === 'suspended') {
    Howler.ctx.resume();
  }
}

// Listen for user interactions to unlock audio (Safari/iOS requirement)
if (typeof document !== 'undefined') {
  const unlock = () => {
    ensureAudioContext();
    document.removeEventListener('click', unlock, true);
    document.removeEventListener('touchstart', unlock, true);
    document.removeEventListener('touchend', unlock, true);
    document.removeEventListener('keydown', unlock, true);
  };
  document.addEventListener('click', unlock, true);
  document.addEventListener('touchstart', unlock, true);
  document.addEventListener('touchend', unlock, true);
  document.addEventListener('keydown', unlock, true);
}

// Background music state
let currentMusic = null;
let currentMusicName = null;

let muted = false;

export const SoundManager = {
  play(name) {
    if (muted) return;
    ensureAudioContext();
    const howl = sfxHowls[name];
    if (howl) howl.play();
  },

  getMusicForGame(gameKey) {
    return GAME_MUSIC[gameKey] || 'playful';
  },

  playMusic(name) {
    if (!MUSIC_BASE || !name) return;
    if (currentMusicName === name && currentMusic) return;
    SoundManager.stopMusic();
    currentMusicName = name;

    // Cache-bust URL to bypass stale immutable cache
    const url = `${MUSIC_BASE}/${name}.mp3?${MUSIC_VERSION}`;

    currentMusic = new Howl({
      src: [url],
      volume: muted ? 0 : MUSIC_VOLUME,
      loop: true,
      html5: true,
      preload: true,
    });

    // Play when loaded (handles both instant and delayed loading)
    if (!muted) {
      currentMusic.once('load', () => {
        ensureAudioContext();
        if (currentMusicName === name && !muted) {
          currentMusic.play();
        }
      });
      // If already loaded, play immediately
      if (currentMusic.state() === 'loaded') {
        ensureAudioContext();
        currentMusic.play();
      }
    }
  },

  playGameMusic(gameKey) {
    const track = SoundManager.getMusicForGame(gameKey);
    SoundManager.playMusic(track);
  },

  stopMusic() {
    if (currentMusic) {
      currentMusic.stop();
      currentMusic.unload();
      currentMusic = null;
      currentMusicName = null;
    }
  },

  setMuted(val) {
    muted = val;
    Howler.mute(val);
  },

  isMuted() {
    return muted;
  },

  toggleMute() {
    muted = !muted;
    Howler.mute(muted);
    return muted;
  }
};
